import { useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { selectEffectiveCoordinates } from '../../slices/locationSlice';
import { GET_FEED } from '../../services/apollo/queries/feedQueries';

/**
 * Feed item types for filtering
 */
export const FEED_TYPES = {
  ALL: 'all',
  LOST: 'lost',
  FOUND: 'found',
  PSA: 'psa',
  PET_SERVICE: 'petService',
  EMERGENCY: 'emergency',
  MY_POSTS: 'myPosts',
};


const DEFAULT_LIMIT = 10;
const DEFAULT_RADIUS_KM = 250;

const normalizeItem = (item) => {
  return {
    id: item._id,
    type: item.type,
    title: item.title,
    description: item.description,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    location: item.location,
    locationName: item.locationName,
    userId: item.userId,
    imageUrl: item.animalImageUrl || item.imageUrl || null,
    // Animal-specific fields
    animalName: item.animalName,
    animalType: item.animalType,
    breed: item.breed,
    colorMarkings: item.colorMarkings,
    sex: item.sex,
    estimatedWeight: item.estimatedWeight,
    reunited: item.reunited,
    approachable: item.approachable,
    // Lost-specific
    dateLost: item.dateLost,
    lastSeen: item.lastSeen,
    rewardAmount: item.rewardAmount,
    // Found-specific
    dateFound: item.dateFound,
    foundNear: item.foundNear,
    // Contact info
    contactName: item.contactName,
    contactPhone: item.contactPhone,
    contactEmail: item.contactEmail,
    // Emergency-specific (NASA)
    nasaEventId: item.nasaEventId,
    categories: item.categories,
    sources: item.sources,
    expiresAt: item.expiresAt,
    extensionCount: item.extensionCount,
    lastExtendedAt: item.lastExtendedAt,
  };
};

/**
 * Custom hook to fetch unified feed with pagination and filtering
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.filter - Filter type from FEED_TYPES (default: 'all')
 * @param {number} options.radiusInKm - Search radius in kilometers (default: 250)
 * @param {number} options.limit - Items per page
 * @returns {Object} Feed data with loading/error states and pagination controls
 */
const useCombinedFeed = (options = {}) => {
  const { 
    filter = FEED_TYPES.ALL,
    radiusInKm = DEFAULT_RADIUS_KM,
    limit = DEFAULT_LIMIT,
  } = options;

  // Memoize coordinates to prevent unnecessary re-renders
  const rawCoordinates = useSelector(selectEffectiveCoordinates);
  const coordinates = useMemo(() => rawCoordinates || [], [rawCoordinates]);
  const hasCoordinates = coordinates.length > 0;

  // GraphQL query with fetchMore for pagination
  const { 
    data, 
    loading, 
    error,
    fetchMore,
    refetch,
  } = useQuery(GET_FEED, {
    variables: {
      userLocation: hasCoordinates ? coordinates : null,
      radiusInKm,
      filter,
      cursor: null,
      limit,
    },
    skip: !hasCoordinates,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Memoize extracted feed data to prevent dependency issues
  const feedResponse = data?.getFeed;
  
  const pageInfo = useMemo(() => {
    return feedResponse?.pageInfo || { nextCursor: null, hasMore: false };
  }, [feedResponse?.pageInfo]);

  const counts = useMemo(() => {
    return feedResponse?.counts || {
      total: 0,
      lost: 0,
      found: 0,
      psa: 0,
      petService: 0,
      emergency: 0,
    };
  }, [feedResponse?.counts]);

  // Normalize items for consistent component consumption
  const normalizedFeed = useMemo(() => {
    const rawItems = feedResponse?.items || [];
    return rawItems.map(normalizeItem);
  }, [feedResponse?.items]);

  /**
   * Load more items (for infinite scroll)
   */
  const loadMore = useCallback(async () => {
    if (!pageInfo.hasMore || !pageInfo.nextCursor || loading) {
      return;
    }

    try {
      await fetchMore({
        variables: {
          cursor: pageInfo.nextCursor,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult;

          return {
            getFeed: {
              ...fetchMoreResult.getFeed,
              items: [
                ...previousResult.getFeed.items,
                ...fetchMoreResult.getFeed.items,
              ],
              counts: previousResult.getFeed.counts,
            },
          };
        },
      });
    } catch (err) {
      console.error('Error loading more feed items:', err);
    }
  }, [fetchMore, pageInfo.hasMore, pageInfo.nextCursor, loading]);

  /**
   * Refresh the entire feed
   */
  const refreshFeed = useCallback(() => {
    if (hasCoordinates) {
      refetch({
        userLocation: coordinates,
        radiusInKm,
        filter,
        cursor: null,
        limit,
      });
    }
  }, [refetch, hasCoordinates, coordinates, radiusInKm, filter, limit]);

  // Calculate loading states
  const isInitialLoading = loading && !data;
  const isLoadingMore = loading && !!data;

  return {
    // Feed data
    feed: normalizedFeed,
    
    // Pagination
    hasMore: pageInfo.hasMore,
    loadMore,
    
    // Loading states
    isLoading: isInitialLoading,
    isLoadingMore,
    
    // Error states
    hasError: !!error,
    error,
    
    // Counts for filter badges
    counts: {
      all: counts.total,
      lost: counts.lost,
      found: counts.found,
      psa: counts.psa,
      petService: counts.petService,
      emergency: counts.emergency,
    },
    
    // Actions
    refetch: refreshFeed,
    
    // Location status
    hasCoordinates,
  };
};

export default useCombinedFeed;
